// M3.4.1 — item types and starter catalogue.
//
// This file only defines DATA: types, the ~30 starter templates, and pure
// helper predicates. Equip / loot / weight / drop logic lives in M3.4.2+.
//
// M7.1 made the runtime catalogue swappable: the static `BUILTIN_ITEM_TEMPLATES`
// is the factory default, and `setItemCatalogue` / `setItemTemplate` mutate a
// Map-backed registry. Server loads from /content/items.json on boot when
// present; admin app + client receive updates via `catalogueUpdate` over WS.

import { z } from "zod";

// =============================================================================
// Equipment slots (14 per PROMPT.md §4.3)
// =============================================================================

export const EQUIP_SLOTS = [
  "Head",
  "Neck",
  "Shoulders",
  "Chest",
  "Waist",
  "Legs",
  "Feet",
  "RightHand",
  "LeftHand",
  "Ring1",
  "Ring2",
  "Cloak",
  "Wrist1",
  "Wrist2",
] as const;

export type EquipSlot = (typeof EQUIP_SLOTS)[number];

// =============================================================================
// Categories, weapon types, affix taxonomy
// =============================================================================

export type ItemCategory =
  | "weapon1H"
  | "weapon2H"
  | "shield"
  | "armor"
  | "ring"
  | "neck"
  | "wrist"
  | "cloak"
  | "consumable"
  | "misc";

export type WeaponType = "lightMelee" | "heavyMelee" | "bow" | "staff";

export type ItemRarity = "common" | "rare" | "unique";

// Affix system (rolled at drop time on rare items, M3.4.4).
// Common items have an empty affix list; rares get 1–3 picks from this pool.
export type AffixType =
  | "stat"
  | "dmgPct"
  | "hp"
  | "critPct"
  | "mana"
  | "resistPct";

export type AffixStatSubType = "str" | "dex" | "con" | "int" | "fai" | "rap";
export type AffixDmgSubType = "melee" | "ranged" | "spell";
// M8 — magic revamp. 6 schools (divine/necromancy/water/fire/air/earth) +
// the physical-damage axis. Mana is now a single pool so no subtype is
// needed for `mana` affixes.
export type AffixResistSubType =
  | "physical"
  | "divine"
  | "necromancy"
  | "water"
  | "fire"
  | "air"
  | "earth";

export type AffixSubType =
  | AffixStatSubType
  | AffixDmgSubType
  | AffixResistSubType;

export interface Affix {
  type: AffixType;
  // Required for type ∈ {"stat", "dmgPct", "mana", "resistPct"}, omitted otherwise.
  subType?: AffixSubType;
  value: number;
}

// =============================================================================
// Consumable effects (potions, food)
// =============================================================================

export type ConsumableEffectKind =
  | "heal"
  | "restoreManaArc"
  | "restoreManaDiv"
  | "food";

export interface ConsumableEffect {
  kind: ConsumableEffectKind;
  amount: number;
}

// =============================================================================
// ItemStats — base stats baked into the template (rare affixes layer on top
// at the instance level).
// =============================================================================

export interface ItemStats {
  // Weapons
  damage?: { min: number; max: number };
  weaponType?: WeaponType;
  // Armor / shields
  defense?: number;
  // Consumables
  consumable?: ConsumableEffect;
}

// =============================================================================
// Equip requirements
// =============================================================================

export interface ItemRequirements {
  str?: number;
  dex?: number;
  con?: number;
  int?: number;
  fai?: number;
  rap?: number;
}

// =============================================================================
// ItemTemplate (static, shared between client and server)
// =============================================================================
//
// Slot conventions:
//   - Single EquipSlot   → goes in that exact slot (Head, Chest, Neck, Cloak, …).
//   - EquipSlot[]        → fills ALL listed slots simultaneously (2H weapons:
//                          ["RightHand", "LeftHand"]).
//   - null               → not equippable (consumables).
//   - Rings / wrists     → carry slot="Ring1" / "Wrist1" by default; the equip
//                          handler (M3.4.2) treats category="ring" as fitting
//                          either Ring1 or Ring2 (same for "wrist"). The slot
//                          field is the "preferred" / canonical one.

export interface ItemTemplate {
  id: string;
  name: string;
  category: ItemCategory;
  slot: EquipSlot | EquipSlot[] | null;
  weight: number; // kg
  requirements: ItemRequirements;
  stats: ItemStats;
  stackable: boolean;
  maxStack: number; // 1 for non-stackable; 99 for potions; etc.
  rarity: ItemRarity; // template-level rarity; rare *instances* are generated at drop time.
  // M5.1: base shop value in copper. Vendors buy from the player at
  // floor(value * sellMultiplier) and sell from a curated stock at full value.
  // Affixes on rare instances do NOT modify the sell-back price for M5.1.
  value: number;
  // M7.1.1: fixed affixes baked into the template. Applied to every instance
  // of this template at effective-stats compute time (merged with any
  // rolled affixes the instance carries). Used for named / unique items
  // ("Belt of Strength always +10 STR") and for any common item the author
  // wants to give a permanent bonus to. `rarity: "unique"` is the canonical
  // tag for these but the field is independent — a common item with one
  // affix is legal too (admin's call).
  affixes?: Affix[];
  // M7.2 — relative path to the inventory icon, served from
  // /client/public/assets/. Omitted = current placeholder rendering
  // (colored slot + name text). Client side missing-file = silent fallback,
  // no console spam.
  iconPath?: string;
}

// =============================================================================
// ItemInstance — a specific drop sitting in inventory / on the ground
// =============================================================================

export interface ItemInstance {
  instanceId: string; // uuid
  templateId: string;
  affixes: Affix[]; // empty for commons, 1–3 for rares
  stackCount: number; // ≥ 1; for non-stackables always 1
}

// =============================================================================
// Starter catalogue (~30 items, spans all 14 slots + 4 weapon types)
// Balance numbers are STARTING POINTS — tunable in playtest, not load-bearing.
// =============================================================================

const t = <T extends Record<string, ItemTemplate>>(o: T): T => o;

// Factory-default catalogue (M3.4.1 starter set). At runtime this is what
// populates the registry when no /content/items.json is loaded. M7.1 made
// the runtime catalogue swappable via setItemCatalogue / setItemTemplate so
// the admin app can publish edits without rebuilding the source.
export const BUILTIN_ITEM_TEMPLATES = t({
  // ---------------------------------------------------------------------------
  // Weapons — 1H melee
  // ---------------------------------------------------------------------------
  rusty_dagger: {
    id: "rusty_dagger",
    name: "Rusty Dagger",
    category: "weapon1H",
    slot: "RightHand",
    weight: 0.5,
    requirements: {},
    stats: { damage: { min: 1, max: 3 }, weaponType: "lightMelee" },
    stackable: false,
    maxStack: 1,
    rarity: "common",
    value: 8,
  },
  iron_sword: {
    id: "iron_sword",
    name: "Iron Sword",
    category: "weapon1H",
    slot: "RightHand",
    weight: 2,
    requirements: { str: 12 },
    stats: { damage: { min: 4, max: 7 }, weaponType: "lightMelee" },
    stackable: false,
    maxStack: 1,
    rarity: "common",
    value: 60,
  },
  steel_sword: {
    id: "steel_sword",
    name: "Steel Sword",
    category: "weapon1H",
    slot: "RightHand",
    weight: 2,
    requirements: { str: 20 },
    stats: { damage: { min: 8, max: 13 }, weaponType: "lightMelee" },
    stackable: false,
    maxStack: 1,
    rarity: "common",
    value: 170,
  },
  iron_mace: {
    id: "iron_mace",
    name: "Iron Mace",
    category: "weapon1H",
    slot: "RightHand",
    weight: 3,
    requirements: { str: 14 },
    stats: { damage: { min: 5, max: 8 }, weaponType: "heavyMelee" },
    stackable: false,
    maxStack: 1,
    rarity: "common",
    value: 55,
  },

  // ---------------------------------------------------------------------------
  // Weapons — 2H melee
  // ---------------------------------------------------------------------------
  iron_battleaxe: {
    id: "iron_battleaxe",
    name: "Iron Battleaxe",
    category: "weapon2H",
    slot: ["RightHand", "LeftHand"],
    weight: 5,
    requirements: { str: 18 },
    stats: { damage: { min: 9, max: 15 }, weaponType: "heavyMelee" },
    stackable: false,
    maxStack: 1,
    rarity: "common",
    value: 195,
  },
  steel_warhammer: {
    id: "steel_warhammer",
    name: "Steel Warhammer",
    category: "weapon2H",
    slot: ["RightHand", "LeftHand"],
    weight: 7,
    requirements: { str: 30 },
    stats: { damage: { min: 18, max: 28 }, weaponType: "heavyMelee" },
    stackable: false,
    maxStack: 1,
    rarity: "common",
    value: 340,
  },

  // ---------------------------------------------------------------------------
  // Weapons — bows (2H)
  // ---------------------------------------------------------------------------
  short_bow: {
    id: "short_bow",
    name: "Short Bow",
    category: "weapon2H",
    slot: ["RightHand", "LeftHand"],
    weight: 1.5,
    requirements: { dex: 12 },
    stats: { damage: { min: 2, max: 5 }, weaponType: "bow" },
    stackable: false,
    maxStack: 1,
    rarity: "common",
    value: 50,
  },
  longbow: {
    id: "longbow",
    name: "Longbow",
    category: "weapon2H",
    slot: ["RightHand", "LeftHand"],
    weight: 2,
    requirements: { dex: 25 },
    stats: { damage: { min: 8, max: 13 }, weaponType: "bow" },
    stackable: false,
    maxStack: 1,
    rarity: "common",
    value: 185,
  },

  // ---------------------------------------------------------------------------
  // Weapons — staves (2H)
  // ---------------------------------------------------------------------------
  apprentice_staff: {
    id: "apprentice_staff",
    name: "Apprentice Staff",
    category: "weapon2H",
    slot: ["RightHand", "LeftHand"],
    weight: 1.5,
    requirements: { int: 10 },
    stats: { damage: { min: 1, max: 3 }, weaponType: "staff" },
    stackable: false,
    maxStack: 1,
    rarity: "common",
    value: 45,
  },
  arcane_staff: {
    id: "arcane_staff",
    name: "Arcane Staff",
    category: "weapon2H",
    slot: ["RightHand", "LeftHand"],
    weight: 2,
    requirements: { int: 25 },
    stats: { damage: { min: 6, max: 10 }, weaponType: "staff" },
    stackable: false,
    maxStack: 1,
    rarity: "common",
    value: 165,
  },

  // ---------------------------------------------------------------------------
  // Shields (LeftHand only)
  // ---------------------------------------------------------------------------
  wooden_shield: {
    id: "wooden_shield",
    name: "Wooden Shield",
    category: "shield",
    slot: "LeftHand",
    weight: 3,
    requirements: {},
    stats: { defense: 2 },
    stackable: false,
    maxStack: 1,
    rarity: "common",
    value: 25,
  },
  iron_shield: {
    id: "iron_shield",
    name: "Iron Shield",
    category: "shield",
    slot: "LeftHand",
    weight: 5,
    requirements: { str: 15 },
    stats: { defense: 6 },
    stackable: false,
    maxStack: 1,
    rarity: "common",
    value: 90,
  },

  // ---------------------------------------------------------------------------
  // Armor — Head
  // ---------------------------------------------------------------------------
  leather_cap: {
    id: "leather_cap",
    name: "Leather Cap",
    category: "armor",
    slot: "Head",
    weight: 0.5,
    requirements: {},
    stats: { defense: 1 },
    stackable: false,
    maxStack: 1,
    rarity: "common",
    value: 18,
  },
  iron_helm: {
    id: "iron_helm",
    name: "Iron Helm",
    category: "armor",
    slot: "Head",
    weight: 2,
    requirements: { str: 13 },
    stats: { defense: 4 },
    stackable: false,
    maxStack: 1,
    rarity: "common",
    value: 65,
  },
  steel_helm: {
    id: "steel_helm",
    name: "Steel Helm",
    category: "armor",
    slot: "Head",
    weight: 3,
    requirements: { str: 22 },
    stats: { defense: 9 },
    stackable: false,
    maxStack: 1,
    rarity: "common",
    value: 115,
  },

  // ---------------------------------------------------------------------------
  // Armor — Chest
  // ---------------------------------------------------------------------------
  leather_tunic: {
    id: "leather_tunic",
    name: "Leather Tunic",
    category: "armor",
    slot: "Chest",
    weight: 2,
    requirements: {},
    stats: { defense: 2 },
    stackable: false,
    maxStack: 1,
    rarity: "common",
    value: 30,
  },
  chain_mail: {
    id: "chain_mail",
    name: "Chain Mail",
    category: "armor",
    slot: "Chest",
    weight: 6,
    requirements: { str: 16 },
    stats: { defense: 7 },
    stackable: false,
    maxStack: 1,
    rarity: "common",
    value: 100,
  },
  plate_mail: {
    id: "plate_mail",
    name: "Plate Mail",
    category: "armor",
    slot: "Chest",
    weight: 10,
    requirements: { str: 28 },
    stats: { defense: 16 },
    stackable: false,
    maxStack: 1,
    rarity: "common",
    value: 190,
  },

  // ---------------------------------------------------------------------------
  // Armor — Legs
  // ---------------------------------------------------------------------------
  leather_pants: {
    id: "leather_pants",
    name: "Leather Pants",
    category: "armor",
    slot: "Legs",
    weight: 1.5,
    requirements: {},
    stats: { defense: 1 },
    stackable: false,
    maxStack: 1,
    rarity: "common",
    value: 25,
  },
  chain_leggings: {
    id: "chain_leggings",
    name: "Chain Leggings",
    category: "armor",
    slot: "Legs",
    weight: 5,
    requirements: { str: 18 },
    stats: { defense: 5 },
    stackable: false,
    maxStack: 1,
    rarity: "common",
    value: 95,
  },

  // ---------------------------------------------------------------------------
  // Armor — Feet
  // ---------------------------------------------------------------------------
  leather_boots: {
    id: "leather_boots",
    name: "Leather Boots",
    category: "armor",
    slot: "Feet",
    weight: 1,
    requirements: {},
    stats: { defense: 1 },
    stackable: false,
    maxStack: 1,
    rarity: "common",
    value: 18,
  },
  iron_boots: {
    id: "iron_boots",
    name: "Iron Boots",
    category: "armor",
    slot: "Feet",
    weight: 2.5,
    requirements: { str: 14 },
    stats: { defense: 3 },
    stackable: false,
    maxStack: 1,
    rarity: "common",
    value: 65,
  },

  // ---------------------------------------------------------------------------
  // Armor — Shoulders & Waist
  // ---------------------------------------------------------------------------
  shoulder_pads: {
    id: "shoulder_pads",
    name: "Leather Shoulder Pads",
    category: "armor",
    slot: "Shoulders",
    weight: 1,
    requirements: {},
    stats: { defense: 1 },
    stackable: false,
    maxStack: 1,
    rarity: "common",
    value: 20,
  },
  iron_pauldrons: {
    id: "iron_pauldrons",
    name: "Iron Pauldrons",
    category: "armor",
    slot: "Shoulders",
    weight: 3,
    requirements: { str: 16 },
    stats: { defense: 4 },
    stackable: false,
    maxStack: 1,
    rarity: "common",
    value: 75,
  },
  leather_belt: {
    id: "leather_belt",
    name: "Leather Belt",
    category: "armor",
    slot: "Waist",
    weight: 0.3,
    requirements: {},
    stats: { defense: 0 },
    stackable: false,
    maxStack: 1,
    rarity: "common",
    value: 12,
  },
  iron_belt: {
    id: "iron_belt",
    name: "Iron Belt",
    category: "armor",
    slot: "Waist",
    weight: 1,
    requirements: { str: 12 },
    stats: { defense: 2 },
    stackable: false,
    maxStack: 1,
    rarity: "common",
    value: 48,
  },

  // ---------------------------------------------------------------------------
  // Cloak / Neck / Ring / Wrist
  // ---------------------------------------------------------------------------
  traveler_cloak: {
    id: "traveler_cloak",
    name: "Traveler's Cloak",
    category: "cloak",
    slot: "Cloak",
    weight: 1,
    requirements: {},
    stats: { defense: 1 },
    stackable: false,
    maxStack: 1,
    rarity: "common",
    value: 22,
  },
  silver_amulet: {
    id: "silver_amulet",
    name: "Silver Amulet",
    category: "neck",
    slot: "Neck",
    weight: 0.2,
    requirements: {},
    stats: {},
    stackable: false,
    maxStack: 1,
    rarity: "common",
    value: 15,
  },
  silver_ring: {
    id: "silver_ring",
    name: "Silver Ring",
    category: "ring",
    slot: "Ring1",
    weight: 0.1,
    requirements: {},
    stats: {},
    stackable: false,
    maxStack: 1,
    rarity: "common",
    value: 12,
  },
  copper_bracer: {
    id: "copper_bracer",
    name: "Copper Bracer",
    category: "wrist",
    slot: "Wrist1",
    weight: 0.3,
    requirements: {},
    stats: { defense: 1 },
    stackable: false,
    maxStack: 1,
    rarity: "common",
    value: 15,
  },

  // ---------------------------------------------------------------------------
  // Consumables (no slot, stackable)
  // ---------------------------------------------------------------------------
  minor_health_potion: {
    id: "minor_health_potion",
    name: "Minor Health Potion",
    category: "consumable",
    slot: null,
    weight: 0.2,
    requirements: {},
    stats: { consumable: { kind: "heal", amount: 50 } },
    stackable: true,
    maxStack: 99,
    rarity: "common",
    value: 6,
  },
  minor_mana_potion: {
    id: "minor_mana_potion",
    name: "Minor Mana Potion",
    category: "consumable",
    slot: null,
    weight: 0.2,
    requirements: {},
    stats: { consumable: { kind: "restoreManaArc", amount: 30 } },
    stackable: true,
    maxStack: 99,
    rarity: "common",
    value: 6,
  },
  bread: {
    id: "bread",
    name: "Loaf of Bread",
    category: "consumable",
    slot: null,
    weight: 0.3,
    requirements: {},
    stats: { consumable: { kind: "food", amount: 10 } },
    stackable: true,
    maxStack: 99,
    rarity: "common",
    value: 2,
  },

  // M5.1 — playtest sandbox item. 50 kg / 1 c is deliberately absurd:
  // exercises the weight gate (3 anvils ≈ 150 kg vs 100 kg cap) without
  // needing to grind currency. `misc` category = no slot, no consume effect.
  anvil: {
    id: "anvil",
    name: "Anvil",
    category: "misc",
    slot: null,
    weight: 50,
    requirements: {},
    stats: {},
    stackable: false,
    maxStack: 1,
    rarity: "common",
    value: 1,
  },
});

// Back-compat alias — existing tests (items.test.ts integrity, npcs.test.ts)
// iterate this. New code should use getItemCatalogue() / getItemTemplate(id)
// to read the live runtime registry instead.
export const ITEM_TEMPLATES = BUILTIN_ITEM_TEMPLATES;
export type ItemTemplateId = keyof typeof BUILTIN_ITEM_TEMPLATES;

// =============================================================================
// Runtime registry (M7.1)
// =============================================================================
//
// All catalogue reads go through this Map so the admin app's "Publish" flow
// can swap entries at runtime without rebuilding. Defaults to the builtin
// catalogue at import time, so tests + cold boot Just Work without any
// JSON file present on disk.

const runtimeItemRegistry = new Map<string, ItemTemplate>(
  Object.entries(BUILTIN_ITEM_TEMPLATES),
);

export function getItemTemplate(id: string): ItemTemplate | undefined {
  return runtimeItemRegistry.get(id);
}

export function getItemCatalogue(): ItemTemplate[] {
  return Array.from(runtimeItemRegistry.values());
}

// Replace the entire item catalogue with a new set. Called by the server
// content loader at boot (if /content/items.json exists) and by the client
// when it receives a catalogueSnapshot / catalogueUpdate over the WS.
export function setItemCatalogue(items: readonly ItemTemplate[]): void {
  runtimeItemRegistry.clear();
  for (const item of items) runtimeItemRegistry.set(item.id, item);
}

// Insert or update a single template. Returns true if it replaced an
// existing entry. Used by the admin "Save" path for incremental edits.
export function setItemTemplate(template: ItemTemplate): boolean {
  const existed = runtimeItemRegistry.has(template.id);
  runtimeItemRegistry.set(template.id, template);
  return existed;
}

// Test-only: drop any edits and re-seed from the builtin catalogue.
export function _resetItemCatalogueForTests(): void {
  runtimeItemRegistry.clear();
  for (const [id, tpl] of Object.entries(BUILTIN_ITEM_TEMPLATES)) {
    runtimeItemRegistry.set(id, tpl);
  }
}

// =============================================================================
// Zod schemas (M7.1) — shared between the server loader and the admin form.
// Validates incoming JSON / form data against the same TypeScript shape.
// =============================================================================

const EquipSlotSchema = z.enum(EQUIP_SLOTS);

const ItemCategorySchema = z.enum([
  "weapon1H",
  "weapon2H",
  "shield",
  "armor",
  "ring",
  "neck",
  "wrist",
  "cloak",
  "consumable",
  "misc",
]);

const WeaponTypeSchema = z.enum(["lightMelee", "heavyMelee", "bow", "staff"]);

const ConsumableEffectSchema = z.object({
  kind: z.enum(["heal", "restoreManaArc", "restoreManaDiv", "food"]),
  amount: z.number().int().nonnegative(),
}).strict();

const ItemStatsSchema = z
  .object({
    damage: z
      .object({
        min: z.number().int().nonnegative(),
        max: z.number().int().nonnegative(),
      })
      .strict()
      .optional(),
    weaponType: WeaponTypeSchema.optional(),
    defense: z.number().int().nonnegative().optional(),
    consumable: ConsumableEffectSchema.optional(),
  })
  .strict();

const ItemRequirementsSchema = z
  .object({
    str: z.number().int().nonnegative().optional(),
    dex: z.number().int().nonnegative().optional(),
    con: z.number().int().nonnegative().optional(),
    int: z.number().int().nonnegative().optional(),
    fai: z.number().int().nonnegative().optional(),
    rap: z.number().int().nonnegative().optional(),
  })
  .strict();

// id constraint: lowercase ASCII, digits, underscores. Same shape the existing
// catalogue follows (rusty_dagger, minor_health_potion, etc.). Long enough to
// be descriptive, short enough to fit in tooltips.
export const ITEM_ID_REGEX = /^[a-z][a-z0-9_]{2,40}$/;

// M7.2 — shared validator for `iconPath` / `spritePath` fields. Relative
// paths only, no traversal (`..`), no absolute URLs. Capped at 200 chars
// since real Kenney filenames hover around 30 chars + nested folders.
export const AssetPathSchema = z
  .string()
  .min(1)
  .max(200)
  .refine((p) => !p.includes(".."), "must not contain '..'")
  .refine(
    (p) => !/^[a-z]+:\/\//i.test(p),
    "must be a relative path, not a URL",
  )
  .refine((p) => !p.startsWith("/"), "must not start with '/'");

// Affix value bounds — broad enough to allow strong uniques (+30 STR rings)
// without letting the admin author absurd numbers by typo.
const AffixSchema = z
  .object({
    type: z.enum(["stat", "dmgPct", "hp", "critPct", "mana", "resistPct"]),
    subType: z
      .enum([
        "str",
        "dex",
        "con",
        "int",
        "fai",
        "rap",
        "melee",
        "ranged",
        "spell",
        "physical",
        "divine",
        "necromancy",
        "water",
        "fire",
        "air",
        "earth",
      ])
      .optional(),
    value: z.number().int().min(-100).max(1000),
  })
  .strict();

export const ItemTemplateSchema = z
  .object({
    id: z.string().regex(ITEM_ID_REGEX),
    name: z.string().min(1).max(60),
    category: ItemCategorySchema,
    slot: z.union([EquipSlotSchema, z.array(EquipSlotSchema).min(1), z.null()]),
    weight: z.number().nonnegative(),
    requirements: ItemRequirementsSchema,
    stats: ItemStatsSchema,
    stackable: z.boolean(),
    maxStack: z.number().int().min(1),
    rarity: z.enum(["common", "rare", "unique"]),
    value: z.number().int().nonnegative(),
    // Optional — omitted = no template-level affixes. Cap of 5 to keep the
    // tooltip readable + the power budget bounded.
    affixes: z.array(AffixSchema).max(5).optional(),
    iconPath: AssetPathSchema.optional(),
  })
  .strict();

export const ItemCatalogueSchema = z.array(ItemTemplateSchema);

export function isWeapon(template: ItemTemplate): boolean {
  return template.category === "weapon1H" || template.category === "weapon2H";
}

export function isTwoHanded(template: ItemTemplate): boolean {
  return template.category === "weapon2H";
}

export function isEquippable(template: ItemTemplate): boolean {
  return template.slot !== null;
}

export function isStackable(template: ItemTemplate): boolean {
  return template.stackable;
}

// All EquipSlots a template would occupy when equipped. Empty for non-equippable
// items. For rings/wrists this returns the canonical (preferred) slot only — the
// equip handler is responsible for choosing Ring1↔Ring2 / Wrist1↔Wrist2.
export function templateOccupies(template: ItemTemplate): readonly EquipSlot[] {
  if (template.slot === null) return [];
  if (Array.isArray(template.slot)) return template.slot;
  return [template.slot];
}

// =============================================================================
// Weight & encumbrance (M3.4.3)
// =============================================================================

// Capacity fraction above which the carry-weight bar turns a warning colour.
// Purely cosmetic — weight has no effect on movement. The only mechanical
// consequence of being heavy is that you can't pick up items past capacity.
export const ENCUMBRANCE_WARN_PCT = 0.8;
// Capacity fraction at which the player is full and can no longer receive
// items (enforced in tryAddInstance / vendorTransact).
export const ENCUMBRANCE_FULL_PCT = 1.0;

export function itemInstanceWeight(instance: { templateId: string; stackCount: number }): number {
  const template = getItemTemplate(instance.templateId);
  if (!template) return 0;
  return template.weight * Math.max(1, instance.stackCount);
}

export function inventoryWeight(inventory: readonly ItemInstance[]): number {
  let total = 0;
  for (const item of inventory) total += itemInstanceWeight(item);
  return total;
}

// Equipped weight, deduped by instanceId so a 2H weapon stored in both
// RightHand and LeftHand only counts once.
export function equippedWeight(
  equipped: Partial<Record<EquipSlot, ItemInstance | null>>,
): number {
  const seen = new Set<string>();
  let total = 0;
  for (const slot of EQUIP_SLOTS) {
    const item = equipped[slot];
    if (!item) continue;
    if (seen.has(item.instanceId)) continue;
    seen.add(item.instanceId);
    total += itemInstanceWeight(item);
  }
  return total;
}

export function totalCarriedWeight(
  inventory: readonly ItemInstance[],
  equipped: Partial<Record<EquipSlot, ItemInstance | null>>,
): number {
  return inventoryWeight(inventory) + equippedWeight(equipped);
}

// True once carried weight is at/over capacity. At that point the player can
// no longer receive items; movement is unaffected.
export function isEncumberedFull(current: number, max: number): boolean {
  return max > 0 && current / max >= ENCUMBRANCE_FULL_PCT;
}

// =============================================================================
// Slot helpers (continued)
// =============================================================================

// Slots that a category accepts (used by the equip handler to know whether a
// ring fits Ring1 OR Ring2, etc.).
export function acceptedSlotsFor(category: ItemCategory): readonly EquipSlot[] {
  switch (category) {
    case "ring":
      return ["Ring1", "Ring2"];
    case "wrist":
      return ["Wrist1", "Wrist2"];
    case "neck":
      return ["Neck"];
    case "cloak":
      return ["Cloak"];
    case "shield":
      return ["LeftHand"];
    case "weapon1H":
      return ["RightHand"];
    case "weapon2H":
      return ["RightHand", "LeftHand"];
    case "armor":
      // Armor templates carry the explicit slot — no derivation possible.
      return [];
    case "consumable":
      return [];
    case "misc":
      return [];
  }
}
